import React, { PureComponent, type ReactNode } from 'react';

import classNames from 'classnames';
import { format, isValid, parseISO } from 'date-fns';
import { isDate, isEqual, isString } from 'lodash-es';

import { DEFAULT_DATE_FORMAT } from '@iconik-io/core/src/date/date_formats';

import ErrorBoundary from '../../components/error_boundary/error_boundary';
import InputNumber from '../../components/fields/input_number/input_number';
import { ICONS } from '../../icons/icon';
import Button from '../button/button';
import type { FormFieldEvent } from '../forms/types';
import { VARIANTS } from '../label/label';
import Calendar from './calendar';
import {
  calendarOptionsDropdown,
  calendarOptionsPresets,
  type DateRange,
  formatNumberValue,
  formatSelectValue,
  matchSelectValue,
  type OptionAndNumber,
  relativeToDate,
} from './default_options';

import mixins from '../../styles/core/mixins.module.css';
import styles from './calendar.module.css';

export type NonStrictDateRange = {
  min?: string;
  max?: string;
};

type Props = {
  optionsPresets: typeof calendarOptionsPresets;
  optionsDropdown: typeof calendarOptionsDropdown;
  relativeToDate: typeof relativeToDate;
  onChange: (value: NonStrictDateRange | string, event: FormFieldEvent) => void;
  matchSelectValue: typeof matchSelectValue;
  formatSelectValue: typeof formatSelectValue;
  formatNumberValue: typeof formatNumberValue;
  showCustomOptions: boolean;
  value?: Date | DateRange | string;
  isRangeMode: boolean;
  maxDate?: Date | string;
  minDate?: Date | string;
  dateFormat: string;
  initialMonth?: Date;
  withTimePicker?: boolean;
};

type State = {
  value?: DateRange | Date | string;
  number?: number;
  option?: string;
  yearMonthPicker?: boolean;
  calendar?: DateRange | Date | number | string;
  range?: DateRange;
};

class DatePicker extends PureComponent<Props, State> {
  static defaultProps: Props = {
    optionsPresets: calendarOptionsPresets,
    optionsDropdown: calendarOptionsDropdown,
    relativeToDate,
    matchSelectValue,
    formatSelectValue,
    formatNumberValue,
    isRangeMode: true,
    onChange: () => {},
    dateFormat: DEFAULT_DATE_FORMAT,
    showCustomOptions: true,
  };

  state: State = this.getStateFromValue(this.props.value);

  calendar = React.createRef<Calendar>();

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.value !== this.props.value) {
      if (!isEqual(prevProps.value, this.props.value)) {
        this.setState(this.getStateFromValue(this.props.value));
      }
    }
  }

  convertValueToDate(value: string | Date | number): Date | string | number {
    const date = new Date(value);
    if (isValid(date)) {
      return date;
    }
    return value;
  }

  getStateFromValue(val: Date | DateRange | string | undefined): State {
    let value;
    if (isString(val) || isDate(val)) {
      const date = parseISO(val.toString());
      value = isValid(date) ? date : val;
      return {
        value,
        calendar: this.getCalendarValue(value),
        ...this.getOptionAndNumber(val),
      };
    }
    const range: DateRange = { ...val };
    if (range.min && range.max) {
      range.min = this.convertValueToDate(range.min);
      range.max = this.convertValueToDate(range.max);
      return {
        range,
        calendar: this.getCalendarValue(range),
        ...this.getOptionAndNumber(val),
      };
    }
    return {};
  }

  getCalendarValue(
    value: Date | DateRange | string | undefined,
  ): DateRange | Date | string | number {
    if (!value) {
      return {};
    }
    if (isDate(value) && isValid(value)) {
      return value;
    }
    if (isString(value)) {
      return this.props.relativeToDate(value);
    }
    if ('min' in value && value.min && value.max) {
      const min = isString(value.min) ? parseISO(value.min) : value.min;
      const max = isString(value.max) ? parseISO(value.max) : value.max;
      if (isValid(min) && isValid(max)) {
        return value;
      }
      return this.props.relativeToDate(value);
    }
    return {};
  }

  onCalendarChange = (
    value: Date | DateRange | undefined,
    event: FormFieldEvent,
  ): void => {
    this.setState({ value, number: undefined, option: undefined }, () => {
      if (this.props.withTimePicker) {
        this.onApplyDatetime(event);
      } else if (!this.props.isRangeMode) {
        this.onApplyDate(event);
      }
    });
  };

  onChangeSelect = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const val = event.target.value;
    const number = this.state.number || 1;
    const value = this.props.formatSelectValue(
      this.props.optionsDropdown[Number(val)].value,
      number,
    );
    this.setState({ value, option: val, number });
    this.setRelativeDateInCalendar(value);
  };

  onChangeNumber = (val: number | undefined | null): void => {
    if (!val) {
      return;
    }
    const option = this.state.option ?? '0';
    const value = this.props.formatNumberValue(
      Number(val),
      this.props.optionsDropdown[Number(option)].value,
    );
    this.setState({ value, number: val, option });
    this.setRelativeDateInCalendar(value);
  };

  onClickPreset = (value: string | DateRange): void => {
    this.setState({ value, option: undefined, number: undefined });
    this.setRelativeDateInCalendar(value);
  };

  onClickOptions = (): void => {
    if (!this.calendar.current) {
      return;
    }
    this.calendar.current.setState({ yearMonthPicker: false });
  };

  setRelativeDateInCalendar(val: Date | DateRange | string): void {
    this.setState({ calendar: this.props.relativeToDate(val) });
  }

  isValueDate(val: DateRange | Date | string | undefined): boolean {
    if (!val) {
      return false;
    }
    if (isDate(val)) {
      return true;
    }
    if (
      !isString(val) &&
      'min' in val &&
      val.min &&
      val.max &&
      isDate(val.min) &&
      isDate(val.max)
    ) {
      return true;
    }
    return false;
  }

  getOptionAndNumber(
    currentValue: Date | DateRange | string | undefined,
  ): OptionAndNumber {
    let value: OptionAndNumber = {};
    this.props.optionsDropdown.forEach((option, i) => {
      if (
        currentValue &&
        !isString(option.value) &&
        !isString(currentValue) &&
        'min' in option.value &&
        'min' in currentValue &&
        option.value.min &&
        option.value.max &&
        currentValue.min &&
        currentValue.max
      ) {
        const matchMin = this.props.matchSelectValue(
          currentValue.min,
          option.value.min,
          i,
        );
        const matchMax = this.props.matchSelectValue(
          currentValue.max,
          option.value.max,
          i,
        );
        if (matchMin) {
          value = matchMin;
        }
        if (matchMax) {
          value = matchMax;
        }
      }
    });
    return value || {};
  }

  onApplyDate = (event: FormFieldEvent): void => {
    const { value } = this.state;
    const { dateFormat, onChange } = this.props;
    if (isDate(value)) {
      onChange(format(value, dateFormat), event);
    }
    if (isString(value)) {
      onChange(value, event);
    }
    if (value && !isString(value) && 'min' in value && value.min && value.max) {
      const min = isString(value.min)
        ? value.min
        : format(value.min, dateFormat);
      const max = isString(value.max)
        ? value.max
        : format(value.max, dateFormat);
      onChange({ min, max }, event);
    }
  };

  onApplyDatetime = (event: FormFieldEvent) => {
    const { value } = this.state;
    const { dateFormat, onChange } = this.props;
    if (isDate(value)) {
      onChange(format(value, dateFormat), event);
    }
  };

  render(): ReactNode {
    const isValueDate = this.isValueDate(this.state.value);
    let presetSelected;
    return (
      <ErrorBoundary>
        <div className={styles.root}>
          <div
            className={classNames(styles.picker, {
              [styles.picker_with_time_picker]: this.props.withTimePicker,
            })}
          >
            {(this.props.optionsPresets.length > 0 ||
              this.props.optionsDropdown.length > 0) &&
              this.props.showCustomOptions && (
                <div
                  onClick={this.onClickOptions}
                  className={classNames(
                    styles.options,
                    !isValueDate && this.state.value
                      ? styles.section_active
                      : undefined,
                  )}
                >
                  {this.props.optionsPresets.length > 0 &&
                    this.props.optionsPresets.map((o, i) => {
                      presetSelected = isEqual(this.state.value, o.value)
                        ? styles.selected
                        : false;
                      return (
                        <button
                          key={i}
                          className={classNames(
                            mixins.reset_button,
                            styles.options_button,
                            presetSelected,
                          )}
                          onClick={(): void => this.onClickPreset(o.value)}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  {this.props.optionsDropdown.length > 0 && (
                    <div
                      className={classNames(
                        styles.relative_picker,
                        this.state.option !== undefined &&
                          this.state.number !== undefined
                          ? styles.selected
                          : undefined,
                      )}
                    >
                      <InputNumber
                        type="number"
                        placeholder="0"
                        className={styles.time_input}
                        onChange={this.onChangeNumber}
                        value={this.state.number || 0}
                        plusIcon={ICONS.Plus}
                        minusIcon={ICONS.Minus}
                        buttonSize="s"
                        buttonVariant={VARIANTS.WHITE}
                        adjustButtonClassName={
                          styles.input_number_adjust_button
                        }
                      />
                      <select
                        onChange={this.onChangeSelect}
                        value={this.state.option ?? ''}
                      >
                        {this.props.optionsDropdown.map((option, index) => (
                          <option key={index} value={String(index)}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

            <div
              className={classNames(
                styles.calendar_container,
                isValueDate ? styles.section_active : undefined,
              )}
            >
              <Calendar
                ref={this.calendar}
                onChange={this.onCalendarChange}
                value={this.state.calendar}
                isRangeMode={this.props.isRangeMode}
                minDate={this.props.minDate}
                maxDate={this.props.maxDate}
                initialMonth={this.props.initialMonth}
                withTimePicker={this.props.withTimePicker}
              />
            </div>
          </div>
          {this.props.isRangeMode && (
            <Button
              uiKey="apply"
              variant="success"
              className={styles.apply}
              onClick={this.onApplyDate}
            >
              Apply Date
            </Button>
          )}
        </div>
      </ErrorBoundary>
    );
  }
}

export default DatePicker;
